import { getPlatformLoginPath } from '@/config/platform'
import { getClientPublicEntryPath, isClientLoginPath } from '@/config/tenant'
import { logoutAdmin } from '@/utils/adminAuth'
import { logoutCliente } from '@/utils/clienteAuth'
import { clearGuestSession } from '@/utils/guestAuth'
import { logoutProfissional } from '@/utils/profissionalAuth'
import { logoutPlatform } from '@/api/platformApi'
import { clearPlatformToken } from '@/utils/platformAdminAuth'

export type AuthRole = 'cliente' | 'profissional' | 'admin' | 'platform'

const NOTICE_KEY = 'agendamentos-app:auth:notice'

type AuthNotice = {
  role: AuthRole
  message: string
}

export function getLoginPathForRole(role: AuthRole): string {
  switch (role) {
    case 'admin':
      return '/admin'
    case 'profissional':
      return '/profissional'
    case 'platform':
      return getPlatformLoginPath()
    case 'cliente':
      return getClientPublicEntryPath()
  }
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, '') || '/'
}

export function isOnLoginPageForRole(role: AuthRole): boolean {
  const path = normalizePath(window.location.pathname)
  if (role === 'admin') return path === '/admin'
  if (role === 'profissional') return path === '/profissional'
  if (role === 'platform') return path === normalizePath(getPlatformLoginPath())
  if (role === 'cliente') return isClientLoginPath(path)
  return false
}

export function clearSessionForRole(role: AuthRole): void {
  switch (role) {
    case 'admin':
      logoutAdmin()
      break
    case 'profissional':
      logoutProfissional()
      break
    case 'platform':
      clearPlatformToken()
      void logoutPlatform()
      break
    case 'cliente':
      logoutCliente()
      clearGuestSession()
      try {
        localStorage.removeItem('agendamentos-app:user')
      } catch {
        /* ignore */
      }
      break
  }
}

/**
 * Termina a sessão da conta e redireciona para o ecrã de login.
 * Usar em respostas 401/403 ou token JWT expirado no cliente.
 */
export function forceRelogin(role: AuthRole, message: string): never {
  setAuthNotice(role, message)
  clearSessionForRole(role)
  if (!isOnLoginPageForRole(role)) {
    window.location.replace(getLoginPathForRole(role))
  }
  throw new Error(message)
}

export function setAuthNotice(role: AuthRole, message: string): void {
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ role, message } satisfies AuthNotice))
  } catch {
    /* ignore */
  }
}

export function consumeAuthNotice(role: AuthRole): string | null {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AuthNotice
    sessionStorage.removeItem(NOTICE_KEY)
    if (parsed?.role !== role) return null
    return parsed?.message || null
  } catch {
    return null
  }
}
